import React, { useState } from 'react';
import { App, Alert, Input, Modal, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/currency';
import { formatUnit } from '../../utils/units';
import { submitWithOperationalException } from '../operationalExceptions/operationalExceptionFlow';
import { getPZErrorMessage } from './pzErrorMessages';
import { useFeedback } from '../../utils/feedback';
import ProductNameWithDimensions from '../../components/products/ProductNameWithDimensions';
import {
  describePZStornoBlockers,
  normalizePZStornoBlockers,
  stornoPZDocument,
  type PZStornoPreview,
  type PZStornoPreviewItem,
} from './pzStornoApi';

const { TextArea } = Input;
const REASON_MIN_LENGTH = 10;
const REASON_MAX_LENGTH = 1000;

interface PZStornoDialogProps {
  open: boolean;
  pzId: string;
  pzNumber: string;
  expectedUpdatedAt: string;
  preview: PZStornoPreview | null;
  onCancel: () => void;
  onDone: () => void;
}

const PZStornoDialog: React.FC<PZStornoDialogProps> = ({
  open,
  pzId,
  pzNumber,
  expectedUpdatedAt,
  preview,
  onCancel,
  onDone,
}) => {
  const { t } = useTranslation(['pz', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const showMoney = preview?.items.some((item) => item.remainingValuePLN !== undefined)
    || preview?.totalRemainingValuePLN !== undefined;

  const handleClose = () => {
    if (submitting) return;
    setReason('');
    setReasonError(null);
    onCancel();
  };

  const handleConfirm = async () => {
    const trimmedReason = reason.trim();
    if (trimmedReason.length < REASON_MIN_LENGTH) {
      setReasonError(t('pz:storno.reasonRequired'));
      return;
    }
    setReasonError(null);
    setSubmitting(true);
    try {
      const result = await submitWithOperationalException(
        (payload) => stornoPZDocument(pzId, payload as {
          reason: string;
          expectedUpdatedAt: string;
        }),
        { reason: trimmedReason, expectedUpdatedAt },
        modal,
        t,
      );
      if (!result) {
        setSubmitting(false);
        return;
      }
      setReason('');
      setReasonError(null);
      onDone();
    } catch (error: any) {
      const code = error?.response?.data?.code;
      if (code === 'PZ_STORNO_BLOCKED_CONSUMED' && error?.response?.data?.blockers) {
        const blockers = normalizePZStornoBlockers(error.response.data.blockers);
        const details = describePZStornoBlockers(blockers, t);
        feedback.error(
          [t('pz:storno.blockedTitle'), ...details, t('pz:storno.blockedHint')].join(' '),
          { sticky: true },
        );
      } else {
        feedback.error(getPZErrorMessage(error, t, 'pz:error.stornoFailed', { pzNumber }), { sticky: true });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: t('pz:storno.product'),
      key: 'product',
      render: (_: unknown, record: PZStornoPreviewItem) => (
        <ProductNameWithDimensions
          name={record.productName || record.productCode || '-'}
          dimensions={record}
          dimensionsTestId="pz-storno-item-dimensions"
        />
      ),
    },
    {
      title: t('pz:storno.quantity'),
      key: 'quantity',
      render: (_: unknown, record: PZStornoPreviewItem) => (
        `${record.quantityRemaining} ${formatUnit(record.unit, 'pl')}`
      ),
    },
    ...(showMoney ? [{
      title: t('pz:storno.value'),
      key: 'value',
      render: (_: unknown, record: PZStornoPreviewItem) => (
        record.remainingValuePLN === undefined ? '—' : formatCurrency(record.remainingValuePLN, 'PLN')
      ),
    }] : []),
  ];

  return (
    <Modal
      title={t('pz:storno.title', { pzNumber })}
      open={open}
      onCancel={handleClose}
      okText={t('pz:storno.confirm')}
      okButtonProps={{ danger: true, loading: submitting }}
      onOk={() => { void handleConfirm(); }}
      cancelText={t('common:cancel')}
      width={640}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        message={t('pz:storno.description')}
        style={{ marginBottom: 16 }}
      />

      {preview && preview.items.length > 0 && (
        <>
          <Typography.Text strong>{t('pz:storno.itemsTitle')}</Typography.Text>
          <Table
            style={{ marginTop: 8, marginBottom: 16 }}
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={preview.items}
            columns={columns}
            summary={() => (
              showMoney && preview.totalRemainingValuePLN !== undefined ? (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>{t('pz:storno.total')}</Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2}>
                    {formatCurrency(preview.totalRemainingValuePLN, 'PLN')}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              ) : null
            )}
          />
        </>
      )}

      {preview?.supplierOrder && (
        <Alert
          type="info"
          showIcon
          message={t('pz:storno.unlocksSupplierOrder', { orderNumber: preview.supplierOrder.orderNumber })}
          style={{ marginBottom: 16 }}
        />
      )}

      <Typography.Text strong>{t('pz:storno.reason')}</Typography.Text>
      <TextArea
        aria-label={t('pz:storno.reason')}
        rows={3}
        maxLength={REASON_MAX_LENGTH}
        showCount
        value={reason}
        onChange={(event) => {
          setReason(event.target.value);
          if (reasonError) setReasonError(null);
        }}
        style={{ marginTop: 8 }}
      />
      {reasonError && (
        <Typography.Text type="danger">{reasonError}</Typography.Text>
      )}
    </Modal>
  );
};

export default PZStornoDialog;
